
import React from 'react';
import { Bell, ChevronDown, Shield, Zap, RefreshCw } from 'lucide-react';
import { Account } from '../types/portfolio';
// 2026-06-06 source-of-truth-full-ui — shared formatters
import { formatUsd, formatPnL, formatPercent, safeFormatTimestamp, formatSyncStateLabel } from '../lib/accountFormatters';

type AnySyncStatus =
  | 'not_connected'
  | 'sync_pending'
  | 'synced'
  | 'sync_failed'
  | string
  | null
  | undefined;

interface PortfolioHeaderProps {
  account: Account;
  onOpenNotifications?: () => void;
  // 2026-06-06 alpaca-paper-real-account-sync — header is render-only,
  // doesn't recompute account fields; receives sync_status so it knows
  // when to call values "live" vs "pending".
  source?: string;
  syncStatus?: AnySyncStatus;
  lastSyncAt?: string | null;
  onRefresh?: () => void;
  refreshing?: boolean;
}

export function PortfolioHeader({
  account,
  onOpenNotifications,
  source,
  syncStatus,
  lastSyncAt,
  onRefresh,
  refreshing,
}: PortfolioHeaderProps) {
  const isSynced = syncStatus === 'synced';
  const isConnected =
    source === 'connected_broker' ||
    source === 'alpaca_paper_self_connected' ||
    syncStatus === 'synced' ||
    syncStatus === 'sync_pending' ||
    syncStatus === 'sync_failed';

  const stateLabel = isSynced
    ? 'Paper trading — live sync'
    : syncStatus === 'sync_pending'
    ? 'Balance sync pending'
    : syncStatus === 'sync_failed'
    ? 'Balance sync unavailable'
    : isConnected
    ? 'Broker connected'
    : 'No broker connected';

  return (
    <div
      data-testid="portfolio-header"
      data-sync-status={String(syncStatus ?? 'unknown')}
      className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-4 border-b border-[var(--card-border)] bg-[var(--header-bg)] sticky top-0 z-10 px-4 md:px-6"
    >
      <div className="flex items-center gap-4">
        <div
          data-testid="portfolio-header-state"
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${
            isSynced
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600'
              : isConnected
              ? 'bg-amber-500/10 border-amber-500/20 text-amber-700'
              : 'bg-slate-500/10 border-slate-500/20 text-slate-500'
          }`}
        >
          <div
            className={`w-2 h-2 rounded-full ${
              isSynced
                ? 'bg-emerald-500 animate-pulse'
                : isConnected
                ? 'bg-amber-500'
                : 'bg-slate-400'
            }`}
          />
          <span className="text-xs font-bold uppercase tracking-wider">
            {stateLabel}
          </span>
        </div>

        {isConnected && (
          <button
            data-testid="portfolio-header-refresh"
            onClick={onRefresh}
            disabled={!onRefresh || !!refreshing}
            className="flex items-center gap-2 px-3 py-1.5 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-full hover:bg-[var(--card-border)] transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh broker data"
          >
            <RefreshCw
              size={14}
              className={`text-[var(--muted-text)] ${refreshing ? 'animate-spin' : ''}`}
            />
            <span className="text-xs font-bold text-[var(--app-text)] uppercase tracking-wider">
              {refreshing ? 'Refreshing…' : 'Refresh Broker Data'}
            </span>
          </button>
        )}
      </div>

      <div className="flex items-center gap-6">
        <div className="hidden lg:flex flex-col items-end">
          <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
            {isSynced && lastSyncAt ? 'Last Synced' : 'Account'}
          </span>
          <div className="flex items-center gap-1 text-[var(--app-text)] font-black italic uppercase text-sm">
            <Shield size={14} />
            {isSynced && lastSyncAt
              ? safeFormatTimestamp(lastSyncAt, { mode: 'time', fallback: '—', invalidFallback: 'Sync timestamp unavailable' })
              : account?.account_number
              ? account.account_number.length > 4
                ? '****' + account.account_number.slice(-4)
                : '—'
              : '—'}
          </div>
          <div data-testid="portfolio-header-sync-state" className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)] mt-1">
            {formatSyncStateLabel({ timestamp: lastSyncAt, sync_status: syncStatus })}
          </div>
        </div>

        <div className="h-8 w-px bg-[var(--card-border)]" />

        <button
          onClick={onOpenNotifications}
          className="relative p-2 text-[var(--muted-text)] hover:text-[var(--app-text)] transition-colors"
        >
          <Bell size={20} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full border-2 border-[var(--header-bg)]" />
        </button>
      </div>
    </div>
  );
}

interface MetricCardProps {
  label: string;
  value: string;
  subValue?: string;
  isPositive?: boolean;
  tooltip?: string;
  testId?: string;
}

function MetricCard({ label, value, subValue, isPositive, tooltip, testId }: MetricCardProps) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col p-4 border-r border-[var(--card-border)] last:border-r-0 min-w-[160px] flex-1 group relative cursor-help"
    >
      <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest mb-1">{label}</span>
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-black text-[var(--app-text)] tracking-tight font-mono">{value}</span>
        {subValue && (
          <span className={`text-xs font-bold italic ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
            {isPositive ? '+' : ''}{subValue}
          </span>
        )}
      </div>
      {tooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
          {tooltip}
        </div>
      )}
    </div>
  );
}

interface MetricsBarProps {
  account: Account;
  // 2026-06-06 alpaca-paper-real-account-sync — when sync_status !== 'synced'
  // every metric renders as an em-dash so the user is never shown stale or
  // fabricated numbers. The Account object is still required because some
  // downstream charts depend on its shape.
  syncStatus?: AnySyncStatus;
  source?: string;
}

const EM_DASH = '—';

function _safeNum(s: string | undefined | null): number | null {
  if (s === undefined || s === null || s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function _fmtUsd(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function PortfolioMetricsBar({ account, syncStatus, source }: MetricsBarProps) {
  const isSynced = syncStatus === 'synced';
  const isConnectedNoSync = !isSynced &&
    (syncStatus === 'sync_pending' || syncStatus === 'sync_failed' ||
     source === 'connected_broker' || source === 'alpaca_paper_self_connected');

  const portfolioValueN = _safeNum(account?.portfolio_value);
  const cashN = _safeNum(account?.cash);
  const buyingPowerN = _safeNum(account?.buying_power);
  const equityN = _safeNum(account?.equity);
  const lastEquityN = _safeNum(account?.last_equity);
  const maintMarginN = _safeNum(account?.maintenance_margin);

  const dailyPnL =
    isSynced && equityN !== null && lastEquityN !== null
      ? equityN - lastEquityN
      : null;
  const dailyPnLPc =
    isSynced && dailyPnL !== null && lastEquityN && lastEquityN !== 0
      ? (dailyPnL / lastEquityN) * 100
      : null;

  const renderValue = (n: number | null): string => {
    if (!isSynced) return EM_DASH;
    if (n === null) return EM_DASH;
    return _fmtUsd(n);
  };

  return (
    <div
      data-testid="portfolio-metrics-bar"
      data-sync-status={String(syncStatus ?? 'unknown')}
      className="flex overflow-x-auto scrollbar-hide bg-[var(--card-bg)] border-b border-[var(--card-border)]"
    >
      <MetricCard
        testId="metric-portfolio-value"
        label="Portfolio Value"
        value={renderValue(portfolioValueN)}
        tooltip={
          isSynced
            ? 'Total value of all holdings + cash'
            : isConnectedNoSync
            ? 'Waiting for Alpaca balance sync'
            : 'No broker connected'
        }
      />
      <MetricCard
        testId="metric-cash"
        label="Cash Balance"
        value={renderValue(cashN)}
        tooltip={
          isSynced
            ? 'Available cash for withdrawal or trading'
            : isConnectedNoSync
            ? 'Waiting for Alpaca balance sync'
            : 'No broker connected'
        }
      />
      <MetricCard
        testId="metric-buying-power"
        label="Buying Power"
        value={renderValue(buyingPowerN)}
        tooltip={
          isSynced
            ? 'Total funds available to purchase securities'
            : isConnectedNoSync
            ? 'Waiting for Alpaca balance sync'
            : 'No broker connected'
        }
      />
      <MetricCard
        testId="metric-day-pnl"
        label="Day P&L"
        value={dailyPnL === null ? EM_DASH : _fmtUsd(dailyPnL)}
        subValue={dailyPnLPc === null ? undefined : `${dailyPnLPc.toFixed(2)}%`}
        isPositive={dailyPnL !== null && dailyPnL >= 0}
        tooltip={
          isSynced
            ? 'Profit or loss since previous market close'
            : isConnectedNoSync
            ? 'Daily P&L will appear after first successful sync'
            : 'No broker connected'
        }
      />
      <MetricCard
        testId="metric-margin"
        label="Margin Used"
        value={renderValue(maintMarginN)}
        tooltip={
          isSynced
            ? 'Amount of equity used as collateral for margin'
            : isConnectedNoSync
            ? 'Waiting for Alpaca balance sync'
            : 'No broker connected'
        }
      />
    </div>
  );
}
